import type React from 'react';
import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useCollectionBinding,
  useTokens,
  RichTextContent,
  EditableSlotGroup,
  EditableCollectionBinding,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { excludeOtherPhotos, otherPhotoUrls } from '../utils/galleryCategory';
import { contrastRatio, getContrastTextColor } from '../utils/luminance';

interface GalleryPhoto {
  image?: string | null;
  original_url?: string | null;
  alt_text?: string | null;
  title?: string | null;
}

interface Amenity {
  title?: string | null;
  description?: string | null;
  group?: string | null;
  [key: string]: unknown;
}

// One frame of a feature-panel slideshow: a resolved image URL plus alt text
// and an optional caption (unused for gallery-sourced slides — photo `title`
// is the category label, not a per-slide caption).
interface Slide {
  image: string;
  alt: string;
  title?: string;
}

// Slides drawn from the (already Other-filtered) gallery, narrowed to a photo
// category ("Community" / "Apartment") by the photo `title`. Both feature
// panels use this as their primary image source.
function galleryCategorySlides(gallery: GalleryPhoto[], category: string): Slide[] {
  const cat = category.trim().toLowerCase();
  return gallery
    .filter((p) => (p.title || '').trim().toLowerCase() === cat)
    .map((p) => ({
      image: (p.image || p.original_url || '').toString(),
      alt: (p.alt_text || p.title || '').toString(),
      title: '',
    }))
    .filter((s) => s.image);
}

// Last-resort fallback: any safe gallery photos, so the slideshow is never
// empty on a property that has photos but no categorized imagery.
function genericGallerySlides(gallery: GalleryPhoto[]): Slide[] {
  return gallery
    .map((p) => ({
      image: (p.image || p.original_url || '').toString(),
      alt: (p.alt_text || p.title || '').toString(),
      title: '',
    }))
    .filter((s) => s.image);
}

// A group's bullet list can run long (some properties list 30+ amenities per
// group). Collapse to the first LIST_VISIBLE rows and reveal the rest behind a
// "View all amenities" toggle — but only when the list is meaningfully longer
// than the cap (LIST_COLLAPSE_THRESHOLD), so the button never hides just a row
// or two. LIST_VISIBLE matches the theme's 10-amenity cap used elsewhere
// (DiscoverAmenities).
const LIST_VISIBLE = 10;
const LIST_COLLAPSE_THRESHOLD = 12;

// Renders one feature panel's amenity list. The multihub `amenities` collection
// is filtered upstream (Community vs Apartment group) and passed in here; each
// item's title becomes a bullet. Kept as a <div class="af-list"><ul> so the
// existing `.af-list ul` / `.af-list li` styling below still applies.
//
// When the list is long enough to collapse, every item still renders into the
// DOM — the collapsed overflow is hidden with CSS (`.af-list-item--hidden`),
// not sliced out of the array — so all amenities stay crawlable and there's no
// SSR/hydration mismatch.
function AmenityFeatureList({ items }: { items: Amenity[] }) {
  const [expanded, setExpanded] = useState(false);
  if (items.length === 0) return null;
  const collapsible = items.length > LIST_COLLAPSE_THRESHOLD;
  return (
    <div className="af-list font-body text-base leading-relaxed text-[var(--vibe-color-text)]">
      <ul>
        {items.map((item, i) => {
          const hidden = collapsible && !expanded && i >= LIST_VISIBLE;
          return (
            <li key={i} className={hidden ? 'af-list-item--hidden' : undefined}>
              {item.title || ''}
            </li>
          );
        })}
      </ul>
      {collapsible && (
        <button
          type="button"
          className="af-list-toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          <span>{expanded ? 'Show less' : 'View all amenities'}</span>
          {expanded ? (
            <ChevronUp aria-hidden="true" size={18} strokeWidth={2.5} />
          ) : (
            <ChevronDown aria-hidden="true" size={18} strokeWidth={2.5} />
          )}
        </button>
      )}
    </div>
  );
}

// ============================================================================
// Slot group definitions (inline literals — required by the schema linter)
//
// The Amenities page below the featured cards alternates two full-bleed image
// "banner" stripes (a wide photo with a translucent band carrying a section
// header + primary CTA) with two image+text feature panels:
//
//   1. Banner  — "Community Features" / See Virtual Tours
//   2. Panel   — "All the amenities you will ever need." (photo left)
//   3. Banner  — "Apartment Features" / Schedule A Tour
//   4. Panel   — "No detail overlooked" (photo right)
// ============================================================================

// --- Banner 1: Community Features -------------------------------------------

const communityBannerImageDef: SlotGroupDefinition = {
  label: 'Community Banner Photo',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[8].image' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[8].alt_text',
      defaultValue: 'Community amenity space',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 8,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'Select a wide, atmospheric photo of a shared community amenity space (lounge, clubhouse, pool deck, or fitness center) that works as a full-width banner background.',
    context:
      'Full-bleed background image for the "Community Features" banner on the Amenities page. A translucent band with a heading and button sits on top, so favor a horizontally-oriented, not-too-busy photo.',
    sourceCollection: 'photo_gallery',
  },
};

const communityBannerCtaDef: SlotGroupDefinition = {
  label: 'Community Banner CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/virtual-tour' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'See Virtual Tours' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// --- Panel 1: All the amenities you will ever need --------------------------
//
// The left-hand image is a slideshow of photo_gallery images categorized as
// "Community" (the same community amenity photos the Gallery page uses), not
// amenity-record `amenity_image`s and not a single editable photo — so there
// is no image slot group here anymore. The captionSettings slot group wraps
// this slideshow so the editor can attach to an always-visible surface.

const communityCtaDef: SlotGroupDefinition = {
  label: 'Community Panel CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/gallery' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'View Gallery' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// --- Banner 2: Apartment Features -------------------------------------------

const apartmentBannerImageDef: SlotGroupDefinition = {
  label: 'Apartment Banner Photo',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[9].image' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[9].alt_text',
      defaultValue: 'Apartment interior',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 9,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'Select a bright, wide interior photo of an apartment home (kitchen, living room, or bedroom) that works as a full-width banner background.',
    context:
      'Full-bleed background image for the "Apartment Features" banner on the Amenities page. A translucent band with a heading and button sits on top, so favor a horizontally-oriented interior shot.',
    sourceCollection: 'photo_gallery',
  },
};

const apartmentBannerCtaDef: SlotGroupDefinition = {
  label: 'Apartment Banner CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Schedule A Tour' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// --- Panel 2: No detail overlooked ------------------------------------------
//
// The right-hand image is a slideshow of photo_gallery images categorized as
// "Apartment" (the same apartment amenity photos the Gallery page uses), not
// amenity-record `amenity_image`s and not a single editable photo — so there
// is no image slot group here anymore.

const apartmentCtaDef: SlotGroupDefinition = {
  label: 'Apartment Panel CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Find Your Space' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// --- Caption visibility (section-wide) --------------------------------------
//
// Gallery-sourced slides can carry an optional on-image caption. Most
// properties don't want it, so captions are OFF by default. This single
// section-level toggle lets a CMS user turn them on per property; its value
// governs BOTH feature-panel slideshows (Community + Apartment). Realized as a
// slot group (not a bare token) so its editable wrapper can attach to the
// always-visible community slideshow rather than rendering a stray "true/false"
// string.
//
// `show` is a stringly-typed boolean ('true' / 'false') — the field type is
// `text` because the schema has no first-class boolean token type; the
// component reads it truthily (=== 'true').
const captionSettingsDef: SlotGroupDefinition = {
  label: 'Amenity Caption Settings',
  fields: {
    show: {
      label: 'Show Amenity Captions',
      type: 'text',
      defaultValue: 'false',
    },
  },
};

// Down-arrow that trails each banner heading, echoing the reference site's
// "scroll to the features below" cue.
function BannerArrow() {
  return (
    <svg className="af-banner-arrow" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 5v14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M19 12l-7 7-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ============================================================================
// Feature-panel slideshow
//
// Replaces the old single feature photo. Auto-advances through the supplied
// slides with a crossfade, pauses on hover, and exposes arrows + swipe
// once there's more than one frame. All slides are stacked so they decode up
// front and the crossfade never flashes the background. An empty slide list
// renders the surface-colored placeholder, matching the old empty state.
// ============================================================================

function AmenitySlideshow({
  slides,
  label,
  showCaptions = false,
}: {
  slides: Slide[];
  label: string;
  showCaptions?: boolean;
}) {
  const count = slides.length;
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  const advance = useCallback(
    (dir: 1 | -1) => {
      if (count === 0) return;
      setActive((p) => (p + dir + count) % count);
    },
    [count],
  );

  // Auto-rotate, but never while hovered/focused (paused) or with one slide.
  useEffect(() => {
    if (count <= 1 || paused) return;
    const id = setInterval(() => setActive((i) => (i + 1) % count), 5000);
    return () => clearInterval(id);
  }, [count, paused]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null) return;
      const dx = e.changedTouches[0].clientX - touchStartX.current;
      touchStartX.current = null;
      if (Math.abs(dx) < 40) return;
      advance(dx < 0 ? 1 : -1);
    },
    [advance],
  );

  if (count === 0) {
    return <div className="af-slideshow af-slideshow--placeholder" />;
  }

  // Clamp in case the slide list shrinks under the current index.
  const safeActive = Math.min(active, count - 1);

  return (
    <div
      className="af-slideshow"
      role="group"
      aria-roledescription="carousel"
      aria-label={label}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {slides.map((s, i) => (
        <div
          key={s.image || i}
          className={`af-slide${i === safeActive ? ' is-active' : ''}`}
          aria-hidden={i === safeActive ? undefined : true}
        >
          <VibesitesImage
            url={s.image}
            alt={s.alt || s.title || ''}
            className="af-slide-img"
            sizes="(max-width: 768px) 100vw, 50vw"
          />
          {showCaptions && s.title ? (
            <div className="af-slide-caption">
              <span>{s.title}</span>
            </div>
          ) : null}
        </div>
      ))}

      {count > 1 && (
        <>
          <button
            type="button"
            className="af-slideshow__arrow af-slideshow__arrow--prev"
            onClick={() => advance(-1)}
            aria-label="Previous slide"
          >
            <ChevronLeft aria-hidden="true" size={20} strokeWidth={2.5} />
          </button>
          <button
            type="button"
            className="af-slideshow__arrow af-slideshow__arrow--next"
            onClick={() => advance(1)}
            aria-label="Next slide"
          >
            <ChevronRight aria-hidden="true" size={20} strokeWidth={2.5} />
          </button>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

export function AmenitiesFeatures() {
  useRevealStyles();
  const communityBannerRef = useScrollReveal<HTMLDivElement>();
  const communityRef = useScrollReveal<HTMLDivElement>();
  const apartmentBannerRef = useScrollReveal<HTMLDivElement>();
  const apartmentRef = useScrollReveal<HTMLDivElement>();

  const communityBannerImage = useSlotGroup('amenitiesfeatures.communityBannerImage', communityBannerImageDef);
  const communityBannerCta = useSlotGroup('amenitiesfeatures.communityBannerCta', communityBannerCtaDef);
  const communityCta = useSlotGroup('amenitiesfeatures.communityCta', communityCtaDef);
  const apartmentBannerImage = useSlotGroup('amenitiesfeatures.apartmentBannerImage', apartmentBannerImageDef);
  const apartmentBannerCta = useSlotGroup('amenitiesfeatures.apartmentBannerCta', apartmentBannerCtaDef);
  const apartmentCta = useSlotGroup('amenitiesfeatures.apartmentCta', apartmentCtaDef);

  // Section-wide caption toggle (off by default). Stringly-typed boolean read
  // truthily; drives the on-image amenity captions in both feature slideshows.
  const captionSettings = useSlotGroup('amenitiesfeatures.captionSettings', captionSettingsDef);
  const showCaptions = (captionSettings.show ?? 'false').toString().trim().toLowerCase() === 'true';

  // Feature lists are driven by the multihub `amenities` collection, split into
  // two filtered views by the item `group`. Two distinct bindings — one per
  // filtered view — per the collection-binding guidance.
  const communityAmenities = useCollectionBinding<Amenity>('amenitiesfeatures.communityAmenities', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });
  const apartmentAmenities = useCollectionBinding<Amenity>('amenitiesfeatures.apartmentAmenities', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });
  const hasCommunityAmenities = communityAmenities.length > 0;
  const hasApartmentAmenities = apartmentAmenities.length > 0;

  // Virtual-tour CTA stays hidden until the property has at least one tour URL.
  const virtualTours = useCollectionBinding<{ virtual_tour_url?: string | null }>('amenitiesfeatures.virtual_tours', {
    collection: 'virtual_tours',
  });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Photos categorized as "Other" are uncategorized junk (listing interiors,
  // stray uploads) and must not appear in a feature image slot. When a slot's
  // resolved image (default/generated/edited) is an "Other" photo, swap in a
  // safe gallery photo instead; distinct fallback indices keep the four slots
  // on different photos. An empty slot still falls through to its placeholder.
  //
  // Both feature slideshows read this same photo_gallery binding, narrowed
  // to "Community" / "Apartment" (see communitySlides / apartmentSlides).
  const gallery = useCollectionBinding<GalleryPhoto>('amenitiesfeatures.photos', {
    collection: 'photo_gallery',
  });
  const safeGallery = useMemo(() => excludeOtherPhotos(gallery), [gallery]);
  const blockedUrls = useMemo(() => otherPhotoUrls(gallery), [gallery]);

  const safeSlotImage = (
    image: string | null | undefined,
    alt: string | null | undefined,
    fallbackIndex: number,
  ): { image: string; alt: string } => {
    const url = (image || '').toString();
    if (!url || !blockedUrls.has(url)) return { image: url, alt: (alt || '').toString() };
    const fb = safeGallery[fallbackIndex] ?? safeGallery[0];
    if (!fb) return { image: '', alt: (alt || '').toString() };
    return {
      image: (fb.image || fb.original_url || '').toString(),
      alt: (fb.alt_text || fb.title || alt || '').toString(),
    };
  };

  const communityBannerImg = safeSlotImage(communityBannerImage.image, communityBannerImage.alt, 0);
  const apartmentBannerImg = safeSlotImage(apartmentBannerImage.image, apartmentBannerImage.alt, 2);

  // Banner headings + their down-arrow (which draws with `currentColor`) sit on
  // the translucent band — `~82% accent` over the banner photo — so the accent
  // tone dominates the surface the text reads against. Some properties ship a
  // dark accent, and the default `--vibe-color-text` is also dark, which made
  // the heading and arrow invisible (black-on-black). Keep the themed text
  // color when it already clears WCAG AA against the accent; otherwise fall
  // back to a luminance-picked black/white so the text + arrow stay legible on
  // any accent. Both banners share the same band tone, so one value drives all.
  const tokens = useTokens();
  const bannerTextColor = useMemo(() => {
    const accent = tokens?.color?.accent;
    if (!accent) return null;
    const themedText = tokens?.color?.text;
    if (themedText && contrastRatio(accent, themedText) >= 4.5) return themedText;
    return getContrastTextColor(accent);
  }, [tokens]);
  const bannerTextStyle = bannerTextColor
    ? ({ ['--af-banner-text' as string]: bannerTextColor } as React.CSSProperties)
    : undefined;

  // Both feature-panel slideshows skip amenity-record `amenity_image`s and
  // read categorized photos from the gallery. Fall back to any safe gallery
  // photo so a panel is never empty on a property that has imagery at all.
  const communitySlides = useMemo<Slide[]>(() => {
    const byCategory = galleryCategorySlides(safeGallery, 'Community');
    return byCategory.length ? byCategory : genericGallerySlides(safeGallery);
  }, [safeGallery]);

  const apartmentSlides = useMemo<Slide[]>(() => {
    // Apartment panel: always use photo_gallery images categorized as
    // "Apartment" (unit interiors / apartment amenities). Do not prefer
    // amenity-record `amenity_image`s — those are a different source and
    // often empty or mismatched. Fall back to any safe gallery photo so
    // the panel is never empty on a property that has imagery at all.
    const byCategory = galleryCategorySlides(safeGallery, 'Apartment');
    return byCategory.length ? byCategory : genericGallerySlides(safeGallery);
  }, [safeGallery]);

  return (
    <section className="w-full bg-[var(--vibe-color-background)]" style={bannerTextStyle}>
      {/* Banner 1 — Community Features (hidden when the property lists no
          Community-group amenities) */}
      {hasCommunityAmenities && (
      <div ref={communityBannerRef} className="reveal-fade-up af-banner">
        <EditableSlotGroup
          groupPath="amenitiesfeatures.communityBannerImage"
          definition={communityBannerImageDef}
          className="af-banner-img-slot"
        >
          {communityBannerImg.image ? (
            <VibesitesImage
              url={communityBannerImg.image}
              alt={communityBannerImg.alt || 'Community amenity space'}
              className="af-banner-bg"
              sizes="100vw"
            />
          ) : (
            <div className="af-banner-bg af-banner-bg--placeholder" />
          )}
        </EditableSlotGroup>
        <div className="af-banner-band">
          <div className="af-banner-inner">
            <div className="af-banner-heading-wrap">
              <RichTextContent
                path="amenitiesfeatures.communityBannerHeading"
                label="Banner Heading"
                defaultValue="<p>Community Features</p>"
                as="h2"
                className="af-banner-heading"
              />
              <BannerArrow />
            </div>
            {hasVirtualTours ? (
            <EditableSlotGroup groupPath="amenitiesfeatures.communityBannerCta" definition={communityBannerCtaDef}>
              <a
                href={communityBannerCta.url || '/virtual-tour'}
                target={communityBannerCta._target || '_self'}
                className="af-cta inline-flex items-center justify-center whitespace-nowrap px-6 py-3 font-heading text-sm md:px-4 md:tracking-[0.1em] lg:px-12 lg:py-4 lg:text-base lg:tracking-[0.15em] uppercase tracking-[0.15em] transition-all"
              >
                {communityBannerCta.label || 'See Virtual Tours'}
              </a>
            </EditableSlotGroup>
            ) : null}
          </div>
        </div>
      </div>
      )}

      {/* Panel 1 — All the amenities you will ever need (image left, copy right) */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <div
          ref={communityRef}
          className="reveal-fade-up grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 items-center"
        >
          <div className="af-image-slot md:order-2 order-1">
            <div className="af-image-wrap">
              {/* The section-wide "Show Amenity Captions" toggle lives here: its
                  editable wrapper attaches to the always-visible slideshow, and
                  its value governs captions on BOTH feature panels. */}
              <EditableSlotGroup
                groupPath="amenitiesfeatures.captionSettings"
                definition={captionSettingsDef}
                className="af-caption-settings-slot"
              >
                <AmenitySlideshow
                  slides={communitySlides}
                  label="Community amenity photos"
                  showCaptions={showCaptions}
                />
              </EditableSlotGroup>
            </div>
          </div>

          <div className="md:order-1 order-2">
            <RichTextContent
              path="amenitiesfeatures.communityHeading"
              label="Heading"
              defaultValue="<p>All the amenities you will ever need.</p>"
              as="h2"
              className="text-[clamp(2rem,8.8vw,3.5rem)] sm:text-[clamp(1rem,4.4vw,2.5rem)] uppercase tracking-wide text-foreground leading-[1.05] mb-6"
              style={{ fontFamily: 'var(--vibe-font-body)', fontWeight: 600 }}
            />
            <EditableCollectionBinding
              bindingPath="amenitiesfeatures.communityAmenities"
              label="Community Amenities"
            >
              <AmenityFeatureList items={communityAmenities} />
            </EditableCollectionBinding>
            <div className="mt-8">
              <EditableSlotGroup groupPath="amenitiesfeatures.communityCta" definition={communityCtaDef}>
                <a
                  href={communityCta.url || '/gallery'}
                  target={communityCta._target || '_self'}
                  className="af-cta inline-flex items-center justify-center whitespace-nowrap px-6 py-3 font-heading text-sm md:px-4 md:tracking-[0.1em] lg:px-12 lg:py-4 lg:text-base lg:tracking-[0.15em] uppercase tracking-[0.15em] transition-all"
                >
                  {communityCta.label || 'View Gallery'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>
        </div>
      </div>

      {/* Banner 2 + Panel 2 — Apartment Features (hidden when the property
          lists no Apartment-group amenities) */}
      {hasApartmentAmenities && (
      <>
      <div ref={apartmentBannerRef} className="reveal-fade-up af-banner">
        <EditableSlotGroup
          groupPath="amenitiesfeatures.apartmentBannerImage"
          definition={apartmentBannerImageDef}
          className="af-banner-img-slot"
        >
          {apartmentBannerImg.image ? (
            <VibesitesImage
              url={apartmentBannerImg.image}
              alt={apartmentBannerImg.alt || 'Apartment interior'}
              className="af-banner-bg"
              sizes="100vw"
            />
          ) : (
            <div className="af-banner-bg af-banner-bg--placeholder" />
          )}
        </EditableSlotGroup>
        <div className="af-banner-band">
          <div className="af-banner-inner">
            <div className="af-banner-heading-wrap">
              <RichTextContent
                path="amenitiesfeatures.apartmentBannerHeading"
                label="Banner Heading"
                defaultValue="<p>Apartment Features</p>"
                as="h2"
                className="af-banner-heading"
              />
              <BannerArrow />
            </div>
            <EditableSlotGroup groupPath="amenitiesfeatures.apartmentBannerCta" definition={apartmentBannerCtaDef}>
              <a
                href={apartmentBannerCta.url || '/schedule-a-tour'}
                target={apartmentBannerCta._target || '_self'}
                className="af-cta inline-flex items-center justify-center whitespace-nowrap px-6 py-3 font-heading text-sm md:px-4 md:tracking-[0.1em] lg:px-12 lg:py-4 lg:text-base lg:tracking-[0.15em] uppercase tracking-[0.15em] transition-all"
              >
                {apartmentBannerCta.label || 'Schedule A Tour'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      {/* Panel 2 — No detail overlooked (copy left, image right) */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <div
          ref={apartmentRef}
          className="reveal-fade-up grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 items-center"
        >
          <div className="md:order-2 order-2">
            <RichTextContent
              path="amenitiesfeatures.apartmentHeading"
              label="Heading"
              defaultValue="<p>No detail overlooked</p>"
              as="h2"
              className="text-[clamp(2rem,8.8vw,3.5rem)] sm:text-[clamp(1rem,4.4vw,2.5rem)] uppercase tracking-wide text-foreground leading-[1.05] mb-6"
              style={{ fontFamily: 'var(--vibe-font-body)', fontWeight: 600 }}
            />
            <EditableCollectionBinding
              bindingPath="amenitiesfeatures.apartmentAmenities"
              label="Apartment Amenities"
            >
              <AmenityFeatureList items={apartmentAmenities} />
            </EditableCollectionBinding>
            <div className="mt-8">
              <EditableSlotGroup groupPath="amenitiesfeatures.apartmentCta" definition={apartmentCtaDef}>
                <a
                  href={apartmentCta.url || '/floor-plans'}
                  target={apartmentCta._target || '_self'}
                  className="af-cta inline-flex items-center justify-center whitespace-nowrap px-6 py-3 font-heading text-sm md:px-4 md:tracking-[0.1em] lg:px-12 lg:py-4 lg:text-base lg:tracking-[0.15em] uppercase tracking-[0.15em] transition-all"
                >
                  {apartmentCta.label || 'Find Your Space'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          <div className="af-image-slot md:order-1 order-1">
            <div className="af-image-wrap">
              <EditableCollectionBinding
                bindingPath="amenitiesfeatures.photos"
                label="Apartment Amenity Photos"
                className="af-photos-slot"
              >
                <AmenitySlideshow
                  slides={apartmentSlides}
                  label="Apartment amenity photos"
                  showCaptions={showCaptions}
                />
              </EditableCollectionBinding>
            </div>
          </div>
        </div>
      </div>
      </>
      )}

      <style>{`
        /* Solid primary pill — matches the site-wide CTA treatment. */
        .af-cta {
          background: var(--bh-cta, var(--vibe-color-primary));
          color: var(--bh-cta-fg, #fff);
          border: 1.5px solid var(--bh-cta, var(--vibe-color-primary));
          border-radius: 9999px;
          white-space: nowrap;
        }
        .af-cta:hover {
          background: color-mix(in srgb, var(--bh-cta, var(--vibe-color-primary)) 88%, #000);
          border-color: color-mix(in srgb, var(--bh-cta, var(--vibe-color-primary)) 88%, #000);
          transform: translateY(-1px);
          box-shadow: 0 6px 18px color-mix(in srgb, var(--bh-cta, var(--vibe-color-primary)) 30%, transparent);
        }

        /* ---- Full-bleed image banner with translucent center band ---- */
        .af-banner {
          position: relative;
          width: 100%;
          height: 52vh;
          min-height: 420px;
          overflow: hidden;
        }
        /* Mobile: shrink the banner to a square so the full-bleed image reads
           1:1 (object-fit: cover fills it). The translucent band still overlays
           the bottom. */
        @media (max-width: 767px) {
          .af-banner {
            height: auto;
            min-height: 0;
            aspect-ratio: 1 / 1;
          }
        }
        .af-banner-img-slot {
          display: block;
          width: 100%;
          height: 100%;
        }
        .af-banner-bg {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        }
        .af-banner-bg--placeholder {
          background: var(--vibe-color-surface);
        }
        .af-banner-band {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          min-height: 45%;
          display: flex;
          align-items: center;
          padding: 2.5rem 0;
          background: color-mix(in srgb, var(--vibe-color-accent) 82%, transparent);
          z-index: 1;
        }
        .af-banner-inner {
          width: 100%;
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1.5rem 2rem;
          flex-wrap: wrap;
        }
        @media (min-width: 640px) {
          .af-banner-inner { padding: 0 1.5rem; }
        }
        @media (min-width: 1024px) {
          .af-banner-inner { padding: 0 2rem; }
        }
        /* --af-banner-text is a luminance-picked color resolved at runtime so
           the heading + arrow stay legible against the accent-toned band; it
           falls back to the themed text color when the component runs without
           the computed value (e.g. SSR before hydration). */
        .af-banner-heading-wrap {
          display: flex;
          align-items: center;
          gap: 1rem;
          color: var(--af-banner-text, var(--vibe-color-text));
        }
        .af-banner-heading {
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.5rem, 5vw, 3rem);
          font-weight: 400;
          line-height: 1.05;
          text-transform: uppercase;
          letter-spacing: 0.02em;
          color: var(--af-banner-text, var(--vibe-color-text));
        }
        .af-banner-arrow {
          flex: none;
          width: clamp(1.5rem, 3.5vw, 2.25rem);
          height: clamp(1.5rem, 3.5vw, 2.25rem);
        }
        @media (max-width: 640px) {
          .af-banner-inner { justify-content: center; text-align: center; }
        }

        /* ---- Feature image slideshow ---- */
        .af-image-wrap {
          width: 100%;
          overflow: hidden;
          border-radius: 0;
        }
        /* The caption-settings EditableSlotGroup wraps the community slideshow.
           Keep it a transparent, full-size block so it doesn't alter layout and
           the tablet height chain (below) still reaches the slideshow. */
        .af-caption-settings-slot,
        .af-photos-slot {
          display: block;
          width: 100%;
        }
        .af-slideshow {
          position: relative;
          width: 100%;
          aspect-ratio: 4 / 3;
          overflow: hidden;
          background: var(--vibe-color-surface);
          touch-action: pan-y;
          -webkit-user-select: none;
          user-select: none;
        }
        .af-slideshow--placeholder {
          background: var(--vibe-color-surface);
        }
        /* Slides are stacked; only the active one is opaque and interactive so
           the crossfade never flashes the surface behind. */
        .af-slide {
          position: absolute;
          inset: 0;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.6s ease;
        }
        .af-slide.is-active {
          opacity: 1;
          pointer-events: auto;
          z-index: 1;
        }
        .af-slide-img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        }
        @media (prefers-reduced-motion: reduce) {
          .af-slide {
            transition: none;
          }
        }

        /* Amenity title caption over the bottom of the active slide. */
        .af-slide-caption {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 2;
          padding: 1.75rem 1rem 0.9rem;
          background: linear-gradient(to top, rgba(0, 0, 0, 0.6), transparent);
          pointer-events: none;
        }
        .af-slide-caption span {
          font-family: var(--vibe-font-heading);
          font-size: clamp(0.9rem, 2.2vw, 1.1rem);
          font-weight: 400;
          line-height: 1.2;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: #fff;
        }

        /* Prev / next arrows — appear only with >1 slide. */
        .af-slideshow__arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 3;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          padding: 0;
          border: none;
          border-radius: 9999px;
          background: rgba(0, 0, 0, 0.4);
          color: #fff;
          cursor: pointer;
          opacity: 0;
          transition: background 0.2s, opacity 0.2s;
        }
        .af-slideshow:hover .af-slideshow__arrow,
        .af-slideshow:focus-within .af-slideshow__arrow {
          opacity: 1;
        }
        .af-slideshow__arrow:hover {
          background: rgba(0, 0, 0, 0.65);
        }
        .af-slideshow__arrow--prev { left: 0.6rem; }
        .af-slideshow__arrow--next { right: 0.6rem; }
        /* Touch devices can't hover — keep the arrows visible there. */
        @media (hover: none) {
          .af-slideshow__arrow { opacity: 1; }
        }

        /* Tablet two-column band: the heading + eight-item bullet list + CTA
           runs tall, so a fixed-ratio image floated with big white gaps above
           and below it. Stretch the slideshow to fill its column height so it
           more or less matches the copy beside it (object-fit: cover handles
           the crop). Overrides the grid's items-center for the image column. */
        @media (min-width: 768px) and (max-width: 1024px) {
          .af-image-slot {
            align-self: stretch;
          }
          .af-image-slot .af-image-wrap {
            height: 100%;
          }
          .af-image-slot .af-caption-settings-slot,
          .af-image-slot .af-photos-slot {
            height: 100%;
          }
          .af-image-slot .af-slideshow {
            height: 100%;
            aspect-ratio: auto;
          }
        }
        @media (max-width: 768px) {
          .af-slideshow {
            aspect-ratio: 16 / 10;
          }
        }

        /* ---- Feature bullet list ---- */
        .af-list ul {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
        }
        .af-list li {
          position: relative;
          padding-left: 1.75rem;
          /* Slightly tighter than the inherited leading-relaxed (1.625) so
             amenities that wrap to a second line don't feel loosely spaced. */
          line-height: 1.5;
        }
        .af-list li::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0.6em;
          width: 0.6rem;
          height: 0.6rem;
          border-radius: 9999px;
          background: #635446;
        }
        /* Collapsed overflow: the item stays in the DOM (crawlable) but is
           pulled out of layout until the "View all amenities" toggle expands. */
        .af-list-item--hidden {
          display: none;
        }
        /* Understated text toggle beneath the list — a chevron + label in
           black so it stays readable against the light section background. */
        .af-list-toggle {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          margin-top: 1rem;
          padding: 0;
          border: none;
          background: none;
          cursor: pointer;
          font-family: var(--vibe-font-heading);
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #000;
          transition: opacity 0.2s ease;
        }
        .af-list-toggle:hover {
          opacity: 0.7;
        }
        .af-list-toggle svg {
          flex: none;
          transition: transform 0.2s ease;
        }
        @media (prefers-reduced-motion: reduce) {
          .af-list-toggle,
          .af-list-toggle svg {
            transition: none;
          }
        }
        /* Mobile: the global bullet line-height tightens these rows, so pull the
           dot smaller and nudge it up to sit on the first line's optical center.
           Explicit equal width/height keeps it a true circle (no vertical
           squish) at the smaller size. */
        @media (max-width: 767px) {
          .af-list li::before {
            width: 0.45rem;
            height: 0.45rem;
            top: 0.45em;
          }
        }
        /* Tablet: drop the bullet type and tighten the row gap so the copy
           column packs down vertically, meeting the taller photo above. */
        @media (min-width: 768px) and (max-width: 1024px) {
          .af-list {
            font-size: 0.875rem;
          }
          .af-list ul {
            gap: 0.5rem;
          }
          .af-list li {
            padding-left: 1.5rem;
          }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesFeatures',
  displayName: 'Amenities Features',
  tokens: {
    'amenitiesfeatures.communityBannerHeading': {
      label: 'Community Banner Heading',
      type: 'richtext',
      defaultValue: '<p>Community Features</p>',
    },
    'amenitiesfeatures.communityHeading': {
      label: 'Community Heading',
      type: 'richtext',
      defaultValue: '<p>All the amenities you will ever need.</p>',
    },
    'amenitiesfeatures.apartmentBannerHeading': {
      label: 'Apartment Banner Heading',
      type: 'richtext',
      defaultValue: '<p>Apartment Features</p>',
    },
    'amenitiesfeatures.apartmentHeading': {
      label: 'Apartment Heading',
      type: 'richtext',
      defaultValue: '<p>No detail overlooked</p>',
    },
  },
  slotGroups: {
    'amenitiesfeatures.communityBannerImage': communityBannerImageDef,
    'amenitiesfeatures.communityBannerCta': communityBannerCtaDef,
    'amenitiesfeatures.communityCta': communityCtaDef,
    'amenitiesfeatures.apartmentBannerImage': apartmentBannerImageDef,
    'amenitiesfeatures.apartmentBannerCta': apartmentBannerCtaDef,
    'amenitiesfeatures.apartmentCta': apartmentCtaDef,
    'amenitiesfeatures.captionSettings': captionSettingsDef,
  },
  collectionBindings: {
    'amenitiesfeatures.communityAmenities': {
      label: 'Community Amenities',
      collection: 'amenities',
      filter: { group: 'Community' },
    },
    'amenitiesfeatures.apartmentAmenities': {
      label: 'Apartment Amenities',
      collection: 'amenities',
      filter: { group: 'Apartment' },
    },
    'amenitiesfeatures.photos': {
      label: 'Apartment Amenity Photos',
      collection: 'photo_gallery',
    },
    'amenitiesfeatures.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(AmenitiesFeatures as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AmenitiesFeatures;
